import { ChangeEventHandler, FC } from 'react';
import {
	Accordion,
	Box,
	Title,
	Flex,
	Button,
	Text,
	Checkbox,
} from '@mantine/core';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SingleAttribute } from '../singleAttribute/SingleAttribute';
import { AttributeType } from '@/api/AttributeApi';
import {
	groupAttributeValues,
	mapAttributesByRelation,
} from '@/utils/creatorUtils';
import { UUID } from '@/types/types';
import { useContent } from '@/providers/ContentProvider';
import AttributeValueApi, { AttributeValue } from '@/api/AttributeValueApi';
import CreatorProductModuleApi from '@/api/CreatorProductModuleApi';

const baseSlug = 'creatorProductModuleConfiguration.';

export const CreatorProductModuleConfiguration: FC = () => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { options, attributesWithValues, project, preset } = useTypedSelector(
		(state) => state.creator
	);

	const modules = options.isPresetMode
		? preset.creatorProductModules
		: project.creatorProductModules;

	const selectedCreatorProductModule = options.selectedCreatorProductModule!;

	const attributeValueOverridesQuery = useQuery({
		queryKey: [
			AttributeValueApi.queryKey,
			selectedCreatorProductModule.productModule!.attributeValueOverridesIds,
		],
		queryFn: AttributeValueApi.findByIds,
		enabled: selectedCreatorProductModule.productModule!.hasValuesOverride,
		select: groupAttributeValues,
		placeholderData: [],
	});

	const handleChebkboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		dispatch(
			creatorActions.updateOptions({
				applyValueToAll: e.currentTarget.checked,
			})
		);
	};

	const deleteCreatorProductModuleMutation = useMutation({
		mutationFn: CreatorProductModuleApi.delete,
		onSuccess: () => {
			dispatch(
				creatorActions.removeCreatorProductModule(selectedCreatorProductModule)
			);
		},
	});

	const handleRemoveModule = () => {
		deleteCreatorProductModuleMutation.mutate(selectedCreatorProductModule.id);
	};

	const handleSelectAttributeValue =
		(attributeId: UUID) => (attributeValue: AttributeValue) => () => {
			if (options.applyValueToAll) {
				const changes = [
					{
						attributeId,
						attributeValue,
					},
				];

				if (attributeValue.relatedValue) {
					const relatedValue = attributesWithValues
						.find(
							(attr) => attr.id === attributeValue.relatedValue?.attributeId
						)
						?.values.find(
							(val) => val.id === attributeValue.relatedValue?.attributeValueId
						);

					if (relatedValue)
						changes.push({
							attributeId: attributeValue.relatedValue.attributeId,
							attributeValue: relatedValue,
						});
				}

				dispatch(creatorActions.handleCollectionAttributeValuesChange(changes));
				return;
			}

			const currentSelectedValue =
				selectedCreatorProductModule.selectedAttributeValues!.find(
					(attr) => attr.attribute.id === attributeId
				);
			if (currentSelectedValue?.id === attributeValue.id) return;

			const attributeValues = [attributeValue];

			if (attributeValue.relatedValue) {
				const relatedValue = attributesWithValues
					.find((attr) => attr.id === attributeValue.relatedValue?.attributeId)
					?.values.find(
						(val) => val.id === attributeValue.relatedValue?.attributeValueId
					);
				if (relatedValue) attributeValues.push(relatedValue);
			}

			dispatch(
				creatorActions.handleCreatorProductModuleConfiguration({
					creatorProductModule: selectedCreatorProductModule,
					attributeValues,
				})
			);
		};

	const isDeleteable =
		modules.length > 1 && selectedCreatorProductModule.deleteable;

	const moduleAttributes = selectedCreatorProductModule.productModule!
		.hasValuesOverride
		? attributeValueOverridesQuery.data!
		: attributesWithValues.filter((attr) => attr.type === AttributeType.MODULE);

	return (
		<Box mt="sm">
			<Flex mb="xs" align="center" justify="space-between">
				<Title order={4}>
					{getContent(`${baseSlug}title`, {
						vars: {
							$name:
								selectedCreatorProductModule.productModule!.storeConfiguration
									.name,
						},
					})}
				</Title>
				{isDeleteable && !options.isPreviewMode && (
					<Button color="red.9" size="sm" onClick={handleRemoveModule}>
						{getContent(`${baseSlug}remove`)}
					</Button>
				)}
			</Flex>
			<Text size="sm" mb="lg" c="dimmed">
				{getContent(`${baseSlug}deselectHint`)}
			</Text>
			<Accordion mt="lg">
				{mapAttributesByRelation(
					moduleAttributes,
					selectedCreatorProductModule.selectedAttributeValues!
				).map((attribute) => {
					const selectedAttributeValue =
						selectedCreatorProductModule.selectedAttributeValues!.find(
							(attr) => attr.attribute.id === attribute.id
						);

					return (
						<SingleAttribute
							key={attribute.id}
							attribute={attribute}
							selectedAttributeValue={selectedAttributeValue}
							handleSelectAttributeValue={handleSelectAttributeValue(
								attribute.id
							)}
						/>
					);
				})}
			</Accordion>
			{!selectedCreatorProductModule.productModule!.hasValuesOverride && (
				<Checkbox
					label={getContent(`${baseSlug}applyToAll`)}
					my="md"
					checked={options.applyValueToAll}
					onChange={handleChebkboxChange}
				/>
			)}
		</Box>
	);
};
